import React, { useState, useEffect } from "react";
import { Row, Col, Spinner } from "reactstrap";
import CountUp from "react-countup";
import FeatherIcon from "feather-icons-react";

/* ─── CSS-variable-based card style (works in dark AND light mode) ─ */
const card = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  padding: "20px 22px",
  transition: "box-shadow .2s, transform .2s",
  minHeight: 140,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const pageIconWrapStyle = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid rgba(var(--vz-primary-rgb), 0.35)",
  background: "rgba(var(--vz-primary-rgb), 0.08)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};


const MetricTiles = () => {
  const [summary, setSummary] = useState({ totalAgents: 0, onlineAgents: 0, offlineAgents: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [credErr, setCredErr] = useState(false);
  const [hovered, setHovered] = useState(null);

  const creds = () => ({ apiUrl: localStorage.getItem("flaskApiUrl"), apiKey: localStorage.getItem("flaskApiKey") });
  const authFetch = async (url) => {
    const { apiUrl, apiKey } = creds();
    if (!apiUrl || !apiKey) throw new Error("Credentials not configured");
    const r = await fetch(`${apiUrl}${url}`, { headers: { "Content-Type": "application/json", "X-API-Key": apiKey } });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || "Request failed");
    return d;
  };

  const fetchSummary = async () => {
    setLoading(true); setError(null); setCredErr(false);
    try {
      const { apiUrl, apiKey } = creds();
      if (!apiUrl || !apiKey) { setCredErr(true); throw new Error("Credentials not configured"); }
      const HIDDEN_AGENT_IDS = ["022"];
      const data = await authFetch("/api/wazuh/agents");
      if (data?.agents) {
        const agents = data.agents.filter(a => !HIDDEN_AGENT_IDS.includes(a.id) && (a.status || "").toLowerCase() !== "unknown");
        const online = agents.filter(a => ["active", "online"].some(s => (a.status || "").toLowerCase().includes(s))).length;
        const never = agents.filter(a => (a.status || "").toLowerCase().includes("never")).length;
        const offline = agents.length - online - never;
        setSummary({ totalAgents: agents.length, onlineAgents: online, offlineAgents: offline });
      }
    } catch (e) {
      setError(e.message);
      if (e.message.includes("Credentials") || e.message.includes("Auth")) setCredErr(true);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchSummary();
    const t = setInterval(fetchSummary, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const tiles = [
    {
      label: "Total Agents",
      value: summary.totalAgents,
      icon: "layers",
      accent: "var(--vz-primary)",
      sub: `${summary.totalAgents} registered`,
    },
    {
      label: "Online Agents",
      value: summary.onlineAgents,
      icon: "check-circle",
      accent: "var(--vz-success)",
      sub: summary.totalAgents > 0
        ? `${Math.round((summary.onlineAgents / summary.totalAgents) * 100)}% of total`
        : "—",
    },
    {
      label: "Offline Agents",
      value: summary.offlineAgents,
      icon: "x-circle",
      accent: summary.offlineAgents > 0 ? "var(--vz-danger)" : "var(--vz-secondary-color)",
      sub: summary.offlineAgents > 0 ? "Requires attention" : "All operational",
    },
  ];

  if (loading) return (
    <Row className="mb-4 g-3">
      {[1, 2, 3].map(k => (
        <Col xl={4} md={6} key={k}>
          <div style={card} className="d-flex align-items-center justify-content-center">
            <Spinner size="sm" color="primary" />
          </div>
        </Col>
      ))}
    </Row>
  );

  return (
    <>
      {error && (
        <div style={{
          padding: "10px 16px",
          background: "rgba(var(--vz-danger-rgb),.1)",
          border: "1px solid rgba(var(--vz-danger-rgb),.3)",
          borderRadius: 8, fontSize: 13,
          color: "var(--vz-danger)",
          marginBottom: 16,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span><FeatherIcon icon="alert-circle" size={14} className="me-2" />{error}</span>
          {credErr && (
            <button
              onClick={() => { localStorage.removeItem("flaskApiUrl"); localStorage.removeItem("flaskApiKey"); window.location.href = "/login"; }}
              style={{ background: "none", border: "none", color: "var(--vz-primary)", fontWeight: 600, cursor: "pointer", textDecoration: "underline", padding: 0, fontSize: 12 }}
            >
              Return to login
            </button>
          )}
        </div>
      )}

      <Row className="mb-4 g-3">
        {tiles.map((t, i) => (
          <Col xl={4} md={6} sm={6} xs={12} key={i}>
            <div
              style={{
                ...card,
                boxShadow: hovered === i ? "0 8px 24px rgba(0,0,0,.12)" : "0 1px 4px rgba(0,0,0,0.06)",
                transform: hovered === i ? "translateY(-3px)" : "none",
              }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Label + icon */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--vz-secondary-color)", margin: 0 }}>
                  {t.label}
                </p>
                <div style={{ width: 38, height: 38, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <div
                    style={{
                      ...pageIconWrapStyle,
                      border: `1px solid rgba(var(--vz-${t.icon === "check-circle" ? "success" : t.icon === "x-circle" ? "danger" : "primary"}-rgb), 0.25)`,
                      background: `rgba(var(--vz-${t.icon === "check-circle" ? "success" : t.icon === "x-circle" ? "danger" : "primary"}-rgb), 0.08)`
                    }}
                  >
                    <FeatherIcon icon={t.icon} size={18} style={{ color: t.accent }} />
                  </div>
                </div>
              </div>

              {/* Value + sub */}
              <div>
                <h3 style={{ fontSize: 32, fontWeight: 800, color: "var(--vz-body-color)", margin: "8px 0 4px", lineHeight: 1 }}>
                  {credErr
                    ? <span style={{ color: "var(--vz-secondary-color)", fontSize: 20 }}>—</span>
                    : <CountUp start={0} end={t.value} duration={1.5} separator="," />
                  }
                </h3>
                <p style={{ fontSize: 12, color: "var(--vz-secondary-color)", margin: 0 }}>
                  {credErr ? "Login required" : t.sub}
                </p>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </>
  );
};

export default MetricTiles;